import { sanitizeFilenameForUpload } from '../utils/files';

let module = angular.module('app.services.account-uploads-service', []);

// Break large broadcast uploads into 100 MB pieces for multi-part upload
const MAX_CHUNK_SIZE = 100 * 1024 * 1024;

// Amazon has a hard-limit on multi-part uploads of 5TB. However, there is a maximum number of
// 10,000 parts. So the limit is the minimum of 5 TB and 10,000 x MAX_CHUNK_SIZE.
const MAX_MULTI_PART_UPLOAD_SIZE = Math.min(5 * 1024 * 1024 * 1024 * 1024, 10000 * MAX_CHUNK_SIZE);

// Put this in GB (and just round down) for the file size rule.
const MAX_MULTI_PART_UPLOAD_SIZE_GB = Math.floor(MAX_MULTI_PART_UPLOAD_SIZE / (1024 * 1024 * 1024));

// How long to be uploading before we start estimating time remaining (10s)
const TIME_REMAINING_THRESHOLD_MS = 10000;

module.service(
  'AccountUploadsService',
  function ($http, $timeout, $q, $window, toaster, Upload, MultiPartUpload) {
    this.uploadConfiguration = function (allowVideo, allowImage, slotName) {
      var result = {};

      result.rules = {
        size: allowVideo ? { max: '5GB' } : { max: '1MB' },
        pattern:
          (allowImage ? '.jpg,.jpeg,.png,.gif' : '') +
          (allowVideo && allowImage ? ',' : '') +
          (allowVideo ? '.mp4' : ''),
      };

      switch (slotName) {
        case 'broadcast':
          result.rules.pattern = '.mp4,.mov';
          result.rules.size = { max: `${MAX_MULTI_PART_UPLOAD_SIZE_GB}GB` };
          result.fileTypeSentence = 'a video';
          result.helpText = '[MP4, MOV]';
          result.showVideoHelp = false;
          break;
        case 'preview': //thumbnail
        case 'poster':
          result.fileTypeSentence = 'an image';
          result.helpText = '[PNG, JPG] [1920x1080px]';
          break;
        case 'preroll':
          result.fileTypeSentence = 'an MP4';
          result.showVideoHelp = true; // XXX: need to figure out how to inject link
          result.helpText = '[Recommend 720p with FastStart]';
          break;
        case 'overlay':
          result.fileTypeSentence = 'a PNG';
          result.helpText = '[up to 1920x1080px] [5MB]';
          result.rules.size = { max: '5MB' };
          result.rules.pattern = '.png';
          break;
        case 'document':
          result.fileTypeSentence = 'a PDF';
          result.helpText = '[5MB]';
          result.rules.size = { max: '5MB' };
          result.rules.pattern = '.pdf';
          break;
          case 'caption':
            result.fileTypeSentence = 'a WebVTT file';
            result.helpText = '(up to 5MB)';
            result.rules.size = { max: '5MB' };
            result.rules.pattern = '.vtt';
            break;
          case 'csv':
            result.fileTypeSentence = 'a CSV file';
            result.helpText = '(up to 5MB)';
            result.rules.size = { max: '5MB' };
            result.rules.pattern = '.csv';
            break;
        case 'podcast-preview':
          result.fileTypeSentence = 'an image';
          result.helpText = '[PNG, JPG] [Min 1400x1400px] [Max 3000x3000px] [12MB] [72dpi] [RGB]';
          result.rules.size = { max: '12MB' };
          break;
          case 'app-assets':
            result.fileTypeSentence = 'an image';
            result.helpText = '[PNG, JPG]'
            break;
        default:
          result.fileTypeSentence = 'a file to upload';
      }

      return result;
    };

    this.searchUnsplash = function (query, page = 1, pageSize = 15) {
      this.unsplashSearching = true;
      this.unsplashResults = null;
      return $http({
        method: 'GET',
        url: 'https://api.unsplash.com/search/photos',
        params: {
          query: query,
          page: page,
          per_page: pageSize,
        },
        headers: {
          'Ignore-Http-Interceptor-Auth': true,
          Authorization: `Client-ID ${UNSPLASH_APP_ID}`,
        },
      }).then((response) => {
        return response.data;
      });
    };

    this.listUploadsForPage = function (p, q, filters, pageSize) {
      return $http
        .get(`${API_ROOT}/account/uploads`, {
          params: _.extend(
            {
              q: q,
              p: Math.max(0, p - 1),
              l: pageSize,
              s: '-created_at',
            },
            filters
          ),
        })
        .then((response) => {
          var pagination = JSON.parse(response.headers('x-pagination'));
          return {
            uploads: response.data,
            pagination: {
              current: p,
              total: pagination.total,
              resultMin: pageSize * (p - 1) + 1,
              resultMax: pageSize * (p - 1) + response.data.length,
            },
          };
        });
    };

    this.deleteUpload = function (upload) {
      return $http.delete(`${API_ROOT}/account/uploads/${upload.id}`);
    };

    this.destroy = function () {
      MultiPartUpload.destroy();
    };

    const monotonicTime = () => {
      if ($window.performance && $window.performance.now) {
        return $window.performance.now();
      }
      return Date.now();
    };

    const updateFileProgress = (file, evt) => {
      // Update the total percentage
      file.progressPct = parseInt((100.0 * evt.loaded) / evt.total);

      // See how long we've been at it
      const now = monotonicTime();
      const elapsedTimeMs = now - file.uploadStartedAt;
      if (elapsedTimeMs < TIME_REMAINING_THRESHOLD_MS) {
        return;
      }

      // If we've been uploading for longer than TIME_REMAINING_THRESHOLD_MS, then start estimating
      // the remaining upload time.
      const totalEstimateMs = (elapsedTimeMs * 1.0 * evt.total) / evt.loaded;
      file.estimatedTimeRemainingMs = totalEstimateMs - elapsedTimeMs;
    };

    this.processFileAndUpload = function (file, slotName, args) {
      var uploadMetaUrl;
      var statusUploaded = 'uploaded';
      var statusFailed = 'error';

      if (slotName == 'broadcast') {
        if (!(args || {}).broadcast_id) {
          throw Error('broadcast_id is required');
        }
        uploadMetaUrl = `${API_ROOT}/account/broadcasts/${args.broadcast_id}/uploads`;
        statusFailed = 'failed'; //XXX: upload_service uses `failed` instead of `error`
      } else {
        uploadMetaUrl = `${API_ROOT}/account/uploads`;
      }

      file.status = 'uploading';
      file.progressPct = 0;
      file.uploadStartedAt = monotonicTime();

      var promise = $http
        .post(uploadMetaUrl, {
          filename: sanitizeFilenameForUpload(file.name),
          mimetype: file.type || 'application/octet-stream',
          mime_type: file.type || 'application/octet-stream',
          filesize: file.size,
          slot: slotName,
          max_chunk_size: MAX_CHUNK_SIZE,
        })
        .then((result) => {
          console.log('Created upload slot', result);

          var uploadId = result.data.id;

          var s3UploadPromise;
          if (result.data.parts && result.data.parts.length > 0) {
            // Multi-part upload
            s3UploadPromise = MultiPartUpload.start(
              `${uploadMetaUrl}/${uploadId}`,
              file,
              result.data.parts
            );
          } else if (result.data.presigned_url) {
            // XXX: Upload.http is required because multipart/form-data doesn't work with presigned PUT
            s3UploadPromise = Upload.http({
              url: result.data.presigned_url,
              headers: {
                'Content-Type': file.type,
              },
              method: 'PUT',
              data: file,
            });
          } else {
            var psp = result.data.signed_upload;
            s3UploadPromise = Upload.upload({
              url: psp.url,
              method: 'POST',
              data: _.extend({}, psp.params, { file: file }),
            });
          }
          return s3UploadPromise.then(
            (resp) => {
              console.log('Successfully uploaded', resp.data);
              file.status = 'done';
              return $http.put(`${uploadMetaUrl}/${uploadId}`, { status: statusUploaded });
            },
            (err) => {
              console.error('Error uploading:', err);
              toaster.pop('warning', `File ${file.name} failed to upload.`);
              file.status = 'error';
              return $http.put(`${uploadMetaUrl}/${uploadId}`, { status: statusFailed });
            },
            (evt) => updateFileProgress(file, evt)
          );
        })
        .then((response) => {
          return response.data; //peel off artifact of using $http rather than ngResource
        });

      return promise;
    };
  }
);

export default module;



// WEBPACK FOOTER //
// ./app/services/account-uploads-service.js